import React from "react"

import { Tabs, TabsList, TabsTrigger } from "@coval/ui"
import { Globe, Phone } from "lucide-react"

import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import { SipAdvancedFields } from "@/components/shared/forms/SipAdvancedFields"
import { useClearMediaEncryptionOutsideSipMode } from "@/hooks/useClearMediaEncryptionOutsideSipMode"
import { useConnectionMode } from "@/hooks/useConnectionMode"

import {
  INBOUND_VOICE_TOOLTIPS,
  PhoneNumberField,
  SipAddressField,
} from "./SimulatorFormFields"

interface MultiPhoneInboundVoiceFieldsProps {
  phoneNumber: string
  onPhoneNumberChange: (value: string) => void
  onConnectionModeChange?: (mode: "phone" | "sip") => void
  phoneNumberIndex?: number
  onPhoneNumberIndexChange?: (value: number) => void
  widebandEnabled?: boolean
  onWidebandChange?: (enabled: boolean) => void
  customSipHeaders?: string
  onCustomSipHeadersChange?: (value: string) => void
  mediaEncryption?: string
  onMediaEncryptionChange?: (value: string) => void
  disabled?: boolean
  className?: string
  hidePlaceholders?: boolean
  hideSipAdvancedFields?: boolean
}

export const MultiPhoneInboundVoiceFields: React.FC<
  MultiPhoneInboundVoiceFieldsProps
> = ({
  phoneNumber,
  onPhoneNumberChange,
  onConnectionModeChange,
  widebandEnabled,
  onWidebandChange,
  customSipHeaders,
  onCustomSipHeadersChange,
  mediaEncryption,
  onMediaEncryptionChange,
  disabled,
  className = "",
  hidePlaceholders = false,
  hideSipAdvancedFields = false,
}) => {
  const { inputMode, handleModeChange } = useConnectionMode({
    phoneNumber,
    onPhoneNumberChange,
    onModeChange: (newMode) => {
      if (newMode === "phone") {
        onWidebandChange?.(false)
      }
      onConnectionModeChange?.(newMode)
    },
  })

  const isSipMode = inputMode === "sip"
  useClearMediaEncryptionOutsideSipMode(
    isSipMode,
    phoneNumber,
    mediaEncryption,
    onMediaEncryptionChange,
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <FieldLabel tooltip={INBOUND_VOICE_TOOLTIPS.connectionType}>
            Connection Type
          </FieldLabel>
          <Tabs value={inputMode} onValueChange={handleModeChange}>
            <TabsList variant="pill" aria-label="Connection Type">
              <TabsTrigger value="phone" variant="pill" disabled={disabled}>
                <Phone className="w-4 h-4" />
                Phone Number
              </TabsTrigger>
              <TabsTrigger value="sip" variant="pill" disabled={disabled}>
                <Globe className="w-4 h-4" />
                SIP Address
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {inputMode === "phone" ? (
          <PhoneNumberField
            hidePlaceholder={hidePlaceholders}
            value={phoneNumber}
            onChange={onPhoneNumberChange}
            disabled={disabled}
            className={className}
            labelTooltip={INBOUND_VOICE_TOOLTIPS.phoneNumber}
          />
        ) : (
          <SipAddressField
            hidePlaceholder={hidePlaceholders}
            value={phoneNumber}
            onChange={onPhoneNumberChange}
            disabled={disabled}
            className={className}
            labelTooltip={INBOUND_VOICE_TOOLTIPS.sipAddress}
          />
        )}
      </div>
      {inputMode === "sip" && !hideSipAdvancedFields && (
        <SipAdvancedFields
          widebandEnabled={widebandEnabled}
          onWidebandChange={onWidebandChange}
          customSipHeaders={customSipHeaders}
          onCustomSipHeadersChange={onCustomSipHeadersChange}
          mediaEncryption={mediaEncryption}
          onMediaEncryptionChange={onMediaEncryptionChange}
          disabled={disabled}
        />
      )}
    </div>
  )
}
